import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AbsenceStatus, EnhancedAbsenceModel } from '@reducers/orm/absence/absence.model';
import { usePermission } from '@sb/shared/permission/data-access';
import { NotificationBadgeComponent } from '@sb/ui/badge';
import { AnchorButtonComponent, ButtonComponent, ButtonGroupComponent } from '@sb/ui/button';
import { EmptyStateComponent, LoadingStateComponent } from '@sb/ui/feedback';
import { IconComponent } from '@sb/ui/icon';
import { SubscriptionPlanDirective } from '@shared/subscription-plan/subscription-plan.directive';
import { PlanType } from '@shiftbase-com/ts-shared/models';
import { parseDate } from '@shiftbase-com/ts-shared/utilities';
import { startOfDay } from 'date-fns';
import { partition } from 'lodash-es';

import type { EmployeeOverviewData } from '../employee-absence-overview.model';
import { AbsenceListComponent } from './absence-list.component';

export enum AbsenceFilter {
  PENDING_OR_OPEN_ENDED = 'PENDING OR APPROVED',
  FUTURE = 'FUTURE',
  PAST = 'PAST',
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'absence-list-section',
  imports: [
    CommonModule,
    TranslatePipe,
    RouterLink,
    SubscriptionPlanDirective,
    AbsenceListComponent,
    NotificationBadgeComponent,
    ButtonGroupComponent,
    ButtonComponent,
    AnchorButtonComponent,
    IconComponent,
    LoadingStateComponent,
    EmptyStateComponent,
  ],
  templateUrl: './absence-list-section.component.html',
})
export class AbsenceListSectionComponent implements OnChanges {
  @Input()
  loadingAbsences: boolean;

  @Input()
  pendingAbsences: EnhancedAbsenceModel[] = [];

  @Input()
  reviewedAbsences: EnhancedAbsenceModel[] = [];

  employee = input<EmployeeOverviewData>();

  @Input()
  selectedYear: string;

  // TODO why the hell does a generic thing like this live in the 'reports' folder?
  planType = PlanType;

  canRequestAbsence = usePermission(() => ({
    permissions: ['Create absentee', 'Create own absentee'],
    userId: this.employee()?.id,
    departments: this.employee()?.departments,
  }));

  absenceFilter: AbsenceFilter = AbsenceFilter.PENDING_OR_OPEN_ENDED;
  absenceFilterOptions = AbsenceFilter;

  reviewedPastAbsences: EnhancedAbsenceModel[] = [];
  reviewedFutureAbsences: EnhancedAbsenceModel[] = [];
  reviewedOpenEndedAbsences: EnhancedAbsenceModel[] = [];

  ngOnChanges(changes: SimpleChanges) {
    if (changes['reviewedAbsences']) {
      // Partition reviewed absences into open ended approved absences and non open ended reviewed absences
      const partitionedAbsences = partition(
        this.reviewedAbsences,
        (absence) => absence.status === AbsenceStatus.APPROVED && absence?.open_ended === true,
      );

      this.reviewedOpenEndedAbsences = partitionedAbsences[0];

      const reviewedAbsences = partitionedAbsences[1];

      const today = startOfDay(new Date());
      this.reviewedFutureAbsences = reviewedAbsences.filter((absence) => parseDate(absence.enddate) >= today);
      this.reviewedPastAbsences = reviewedAbsences.filter((absence) => parseDate(absence.enddate) < today).reverse();
    }
  }

  get pendingOrOpenEndedCount(): number {
    return this.pendingAbsences.length + this.reviewedOpenEndedAbsences.length;
  }
}
